import { defaultBlockConfig } from './blockCatalog';
import { defaultIfElseConfig } from './ifElseConfig';
import type {
  AutomationActionType,
  AutomationGraph,
  AutomationGraphEdge,
  AutomationGraphNode,
  AutomationTrigger,
} from '../types/AutomationWorkflow.types';

export const START_NODE_ID = 'start';
export const END_NODE_ID = 'end';

export const isStartNode = (node: AutomationGraphNode): boolean =>
  node.type === 'start' || node.type === 'trigger';

export const isEndNode = (node: AutomationGraphNode): boolean =>
  node.type === 'end';

export const isActionNode = (node: AutomationGraphNode): boolean =>
  !isStartNode(node) && !isEndNode(node);

export const getStartNode = (
  graph: AutomationGraph | null | undefined,
): AutomationGraphNode | undefined =>
  graph?.nodes?.find((node) => isStartNode(node));

export const getEndNode = (
  graph: AutomationGraph | null | undefined,
): AutomationGraphNode | undefined =>
  graph?.nodes?.find((node) => isEndNode(node));

export const normalizeGraphNodes = (
  graph: AutomationGraph,
  trigger: AutomationTrigger,
): AutomationGraph => {
  const nodes = [...(graph.nodes ?? [])];
  let edges = [...(graph.edges ?? [])];

  const startIndex = nodes.findIndex((node) => isStartNode(node));
  if (startIndex >= 0) {
    const start = nodes[startIndex];
    nodes[startIndex] = {
      ...start,
      id: START_NODE_ID,
      type: 'start',
      config: {
        triggerType: trigger.type,
        ...trigger.config,
        ...start.config,
      },
    };
    edges = edges.map((edge) => ({
      ...edge,
      from: edge.from === start.id ? START_NODE_ID : edge.from,
      to: edge.to === start.id ? START_NODE_ID : edge.to,
    }));
  } else {
    nodes.unshift({
      id: START_NODE_ID,
      type: 'start',
      position: { x: 80, y: 160 },
      config: { triggerType: trigger.type, ...trigger.config },
    });
  }

  if (!nodes.some((node) => isEndNode(node))) {
    nodes.push({
      id: END_NODE_ID,
      type: 'end',
      position: { x: 720, y: 160 },
      config: {},
    });
  }

  edges = edges.filter(
    (edge) =>
      edge.from !== edge.to &&
      nodes.some((node) => node.id === edge.from) &&
      nodes.some((node) => node.id === edge.to),
  );

  return { nodes, edges };
};

export const getOutgoingEdges = (
  graph: AutomationGraph,
  nodeId: string,
): AutomationGraphEdge[] =>
  (graph.edges ?? []).filter((edge) => edge.from === nodeId);

export const getIncomingEdges = (
  graph: AutomationGraph,
  nodeId: string,
): AutomationGraphEdge[] =>
  (graph.edges ?? []).filter((edge) => edge.to === nodeId);

export const getExecutionOrder = (
  graph: AutomationGraph | null | undefined,
): AutomationGraphNode[] => {
  if (!graph?.nodes?.length) return [];
  const start = getStartNode(graph);
  if (!start) return [];

  const ordered: AutomationGraphNode[] = [];
  const visited = new Set<string>();
  const queue = [start.id];

  while (queue.length) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = graph.nodes.find((item) => item.id === nodeId);
    if (node && isActionNode(node)) ordered.push(node);

    getOutgoingEdges(graph, nodeId).forEach((edge) => {
      if (!visited.has(edge.to)) queue.push(edge.to);
    });
  }

  return ordered;
};

/** Action nodes in run order; falls back to canvas order if edges are missing. */
export const getLinearActionNodes = (
  graph: AutomationGraph | null | undefined,
): AutomationGraphNode[] => {
  if (!graph?.nodes?.length) return [];
  const fromEdges = getExecutionOrder(graph);
  if (fromEdges.length > 0) return fromEdges;
  return graph.nodes
    .filter((node) => isActionNode(node))
    .sort((a, b) => a.position.x - b.position.x);
};

export const syncLinearWorkflowEdges = (
  graph: AutomationGraph,
  actionIds?: string[],
): AutomationGraph => {
  const ids = actionIds ?? getLinearActionNodes(graph).map((node) => node.id);
  const edges: AutomationGraphEdge[] = [];
  let previousId = START_NODE_ID;

  ids.forEach((nodeId) => {
    edges.push({
      id: `edge-${previousId}-${nodeId}`,
      from: previousId,
      to: nodeId,
    });
    previousId = nodeId;
  });

  if (getEndNode(graph)) {
    edges.push({
      id: `edge-${previousId}-${END_NODE_ID}`,
      from: previousId,
      to: END_NODE_ID,
    });
  }

  return { ...graph, edges };
};

export const appendLinearAction = (
  graph: AutomationGraph | null | undefined,
  actionType: AutomationActionType,
): AutomationGraph => {
  const withNode = addActionBlock(graph, actionType);
  const previousIds = getLinearActionNodes(graph).map((node) => node.id);
  const newNode = withNode.nodes
    .filter((node) => isActionNode(node))
    .find((node) => !previousIds.includes(node.id));
  const nextIds = newNode ? [...previousIds, newNode.id] : previousIds;
  return syncLinearWorkflowEdges(withNode, nextIds);
};

export const removeLinearAction = (
  graph: AutomationGraph,
  nodeId: string,
): AutomationGraph => {
  const nextIds = getLinearActionNodes(graph)
    .map((node) => node.id)
    .filter((id) => id !== nodeId);
  const nodes = graph.nodes.filter((node) => node.id !== nodeId);
  return syncLinearWorkflowEdges({ ...graph, nodes }, nextIds);
};

export const moveLinearAction = (
  graph: AutomationGraph,
  nodeId: string,
  direction: 'up' | 'down',
): AutomationGraph => {
  const ids = getLinearActionNodes(graph).map((node) => node.id);
  const index = ids.indexOf(nodeId);
  if (index < 0) return graph;

  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= ids.length) return graph;

  const nextIds = [...ids];
  [nextIds[index], nextIds[targetIndex]] = [
    nextIds[targetIndex],
    nextIds[index],
  ];
  return syncLinearWorkflowEdges(graph, nextIds);
};

/** Splices a dragged step in at the target step's index. */
export const reorderLinearAction = (
  graph: AutomationGraph,
  draggedId: string,
  targetId: string,
): AutomationGraph => {
  if (draggedId === targetId) return graph;

  const ids = getLinearActionNodes(graph).map((node) => node.id);
  if (!ids.includes(draggedId) || !ids.includes(targetId)) return graph;

  const nextIds = ids.filter((id) => id !== draggedId);
  nextIds.splice(nextIds.indexOf(targetId), 0, draggedId);
  return syncLinearWorkflowEdges(graph, nextIds);
};

export const ensureLinearWorkflow = (
  graph: AutomationGraph | null | undefined,
): AutomationGraph => {
  if (!graph?.nodes?.length) return createBlankAutomationGraphFallback();
  return syncLinearWorkflowEdges(graph);
};

const createBlankAutomationGraphFallback = (): AutomationGraph => ({
  nodes: [
    {
      id: START_NODE_ID,
      type: 'start',
      position: { x: 80, y: 160 },
      config: { triggerType: 'manual' },
    },
    {
      id: END_NODE_ID,
      type: 'end',
      position: { x: 720, y: 160 },
      config: {},
    },
  ],
  edges: [
    {
      id: `edge-${START_NODE_ID}-${END_NODE_ID}`,
      from: START_NODE_ID,
      to: END_NODE_ID,
    },
  ],
});

export const addActionBlock = (
  graph: AutomationGraph | null | undefined,
  actionType: AutomationActionType,
  position?: { x: number; y: number },
): AutomationGraph => {
  const safeGraph = graph?.nodes
    ? graph
    : { nodes: [], edges: [] as AutomationGraph['edges'] };
  const actionCount = safeGraph.nodes.filter((node) =>
    isActionNode(node),
  ).length;
  const nodeId = `${actionType}-${actionCount + 1}`;
  const newNode: AutomationGraphNode = {
    id: nodeId,
    type: actionType,
    position: position ?? { x: 280 + actionCount * 220, y: 160 },
    config:
      actionType === 'if_else'
        ? defaultIfElseConfig()
        : defaultBlockConfig(actionType),
  };

  return {
    nodes: [...safeGraph.nodes, newNode],
    edges: safeGraph.edges,
  };
};

export const removeNode = (
  graph: AutomationGraph,
  nodeId: string,
): AutomationGraph => {
  const target = graph.nodes.find((node) => node.id === nodeId);
  if (!target || isStartNode(target) || isEndNode(target)) return graph;

  const incoming = graph.edges.filter((edge) => edge.to === nodeId);
  const outgoing = graph.edges.filter((edge) => edge.from === nodeId);
  const remainingEdges = graph.edges.filter(
    (edge) => edge.from !== nodeId && edge.to !== nodeId,
  );

  incoming.forEach((inEdge) => {
    outgoing.forEach((outEdge) => {
      remainingEdges.push({
        id: `edge-${inEdge.from}-${outEdge.to}`,
        from: inEdge.from,
        to: outEdge.to,
      });
    });
  });

  return {
    nodes: graph.nodes.filter((node) => node.id !== nodeId),
    edges: remainingEdges,
  };
};

export const connectNodes = (
  graph: AutomationGraph,
  fromId: string,
  toId: string,
): AutomationGraph => {
  if (fromId === toId) return graph;
  const fromNode = graph.nodes.find((node) => node.id === fromId);
  const toNode = graph.nodes.find((node) => node.id === toId);
  if (!fromNode || !toNode || isEndNode(fromNode) || isStartNode(toNode))
    return graph;

  const withoutExistingFrom = graph.edges.filter(
    (edge) => edge.from !== fromId,
  );
  return {
    ...graph,
    edges: [
      ...withoutExistingFrom,
      { id: `edge-${fromId}-${toId}`, from: fromId, to: toId },
    ],
  };
};

export const disconnectEdge = (
  graph: AutomationGraph,
  edgeId: string,
): AutomationGraph => ({
  ...graph,
  edges: graph.edges.filter((edge) => edge.id !== edgeId),
});

export const updateNodePosition = (
  graph: AutomationGraph,
  nodeId: string,
  position: { x: number; y: number },
): AutomationGraph => ({
  ...graph,
  nodes: graph.nodes.map((node) =>
    node.id === nodeId ? { ...node, position } : node,
  ),
});

export type WorkflowValidationIssue = {
  nodeId?: string;
  message: string;
};

export const validateWorkflowGraph = (
  graph: AutomationGraph | null | undefined,
  trigger: AutomationTrigger,
): WorkflowValidationIssue[] => {
  const issues: WorkflowValidationIssue[] = [];
  if (!graph?.nodes?.length) {
    issues.push({ message: 'Workflow graph is missing.' });
    return issues;
  }

  const actions = getExecutionOrder(graph);

  if (actions.length === 0) {
    issues.push({
      message: 'Add at least one action to your workflow.',
    });
  }

  if (trigger.type === 'file_added') {
    const folderPath = String(trigger.config.folderPath ?? '').trim();
    if (!folderPath) {
      issues.push({
        nodeId: START_NODE_ID,
        message: 'Choose a folder for the file-added trigger.',
      });
    }
  }

  actions.forEach((node) => {
    switch (node.type) {
      case 'copy_move_file':
        if (!String(node.config.destinationFolder ?? '').trim()) {
          issues.push({
            nodeId: node.id,
            message: 'Copy / move needs a destination folder.',
          });
        }
        break;
      case 'add_to_serato_crate':
        if (!String(node.config.crateName ?? '').trim()) {
          issues.push({
            nodeId: node.id,
            message: 'Serato crate action needs a crate name.',
          });
        }
        break;
      case 'run_agent':
        if (!String(node.config.instructions ?? '').trim()) {
          issues.push({
            nodeId: node.id,
            message: 'Ask Crate Hackers action needs instructions.',
          });
        }
        break;
      case 'if_else': {
        const minBpm = Number(node.config.minBpm ?? 0);
        const maxBpm = Number(node.config.maxBpm ?? 0);
        if (!Number.isFinite(minBpm) || !Number.isFinite(maxBpm)) {
          issues.push({
            nodeId: node.id,
            message: 'If / else needs a valid BPM range.',
          });
        }
        const trueCrate = String(
          (
            node.config.trueBranch as
              | { config?: { crateName?: string } }
              | undefined
          )?.config?.crateName ?? '',
        ).trim();
        const falseCrate = String(
          (
            node.config.falseBranch as
              | { config?: { crateName?: string } }
              | undefined
          )?.config?.crateName ?? '',
        ).trim();
        const trueType = String(
          (node.config.trueBranch as { type?: string } | undefined)?.type ?? '',
        );
        const falseType = String(
          (node.config.falseBranch as { type?: string } | undefined)?.type ??
            '',
        );
        if (trueType === 'add_to_serato_crate' && !trueCrate) {
          issues.push({
            nodeId: node.id,
            message: 'If branch needs a Serato crate name.',
          });
        }
        if (falseType === 'add_to_serato_crate' && !falseCrate) {
          issues.push({
            nodeId: node.id,
            message: 'Otherwise branch needs a Serato crate name.',
          });
        }
        break;
      }
      default:
        break;
    }
  });

  return issues;
};

export const getNodeValidationIssues = (
  graph: AutomationGraph | null | undefined,
  trigger: AutomationTrigger,
  nodeId: string,
): WorkflowValidationIssue[] => {
  if (!graph?.nodes?.length) {
    return [{ nodeId, message: 'Workflow graph is missing.' }];
  }

  return validateWorkflowGraph(graph, trigger).filter(
    (issue) => issue.nodeId === nodeId,
  );
};
